/**
 * @file
 * This file contains the ProductFormManager class, which is responsible for managing
 * the user interface and interactions on a product detail page (PDP).
 *
 * Key functionalities include:
 * - Handling product variant selections and updating the UI accordingly (e.g., price,
 *   images, pre-order status, and add-to-cart button text).
 * - Managing complex product types like bundles, calculating total prices, and
 *   updating availability.
 * - Implementing special features like the "Top Sheet" optional add-on, which
 *   involves price adjustments and custom add-to-cart logic.
 * - Attaching and managing all necessary event listeners for the product form.
 */
/*============================================================================
  Product template
==============================================================================*/
class ProductFormManager {
  constructor(element) {

    this.container = element;
    this.sectionType = this.container.dataset.sectionType;

    this._attachListeners();
  }


  _onVariantSelected(event) {

    const selected = event.target;

    const productForm = selected.closest("form");

    const preorderMessageNode = productForm.querySelector(
      ".product-form__preorder-message"
    );
    const addToCartButton = productForm.querySelector(
      "button[data-add-to-cart]"
    );
    const slidesContainer = this.container.querySelector(
      ".product__page .product__slides"
    );
    const preorderPropertyInput = productForm.querySelector('[data-preorder-property]'); // prettier-ignore


    let value = selected.value;

    // Clear all values
    selected
      .closest(".selector-wrapper")
      .querySelectorAll(".radio__fieldset")
      .forEach((field) => {
        slidesContainer.classList.remove("product__slides--preorder");
        field.querySelector(".radio__legend__label--current").innerHTML = "";
        addToCartButton.textContent = "ADD TO CART";
        preorderMessageNode.textContent = "";
        preorderPropertyInput.value = false;
      });

    // Set the actual value in the corresponding fieldset
    selected
      .closest(".radio__fieldset")
      .querySelector(".radio__legend__label--current").innerHTML = value;

    // Let's find the variant selected in the master select
    var selectedOption = "";
    var selectedVariantName = "";
    selected
      .closest(".product__selectors")
      .querySelectorAll(
        'input[type="radio"]:not(.optional-product__radio):checked'
      )
      .forEach((option) => {
        if (selectedOption == "") {
          selectedOption = option.value;
        } else {
          selectedOption = selectedOption + " / " + option.value;
        }
      });
    selectedVariantName = selectedOption;
    selectedOption = selectedOption.replaceAll('"', '\\"');

    let option = selected
      .closest(".product-form")
      .querySelector(
        `select.product__form__select--info option[data-value="${selectedOption}"]`
      );

    console.log('  _onVariantSelected: Matched master select option:', option);
    if (option && option.dataset.showPreorder === "true") {
      preorderMessageNode.textContent = option.dataset.preorderMessage;
      preorderPropertyInput.value = true;

      addToCartButton.textContent = "PRE-ORDER";
      slidesContainer.classList.add("product__slides--preorder");
    }

    if (option) {
      let bundleImage = option.dataset.bundleImage;
      let bundleLink = option.dataset.bundleLink;
      let bundleTitle = option.dataset.bundleTitle;
      let bundleText = option.dataset.bundleText;

      if (bundleImage && bundleLink) {
        let bundleContainer = document.querySelector(
          ".product__bundle-box-container"
        );
        if (bundleContainer) {
          bundleContainer.querySelector("img").src = bundleImage;
          bundleContainer.querySelector("a").href = bundleLink;
        } else {
          let html =
            '<div class="product__bundle-box-container product__bundle-box-container--two-columns">' +
            '<div class="product__bundle-box-image">' +
            '<img src="' +
            bundleImage +
            '" />' +
            "</div>" +
            '<div class="product__bundle-box-content">' +
            '<h3 class="product__bundle-box-pretitle">buy together</h3>';
          if (bundleTitle)
            html +=
              '<h4 class="product__bundle-box-title small">' +
              bundleTitle +
              "</h4>";
          if (bundleText)
            html +=
              '<div class="product__bundle-box-text small">' +
              bundleText +
              "</div>";
          if (bundleLink)
            html +=
              '<a href="' +
              bundleLink +
              '" class="product__bundle-box-link">Bundle + Save</a>';
          html += "</div>" + "</div>";
          document.querySelector(".product__bundle-box-wrapper").innerHTML =
            html;
        }
      } else {
        if (document.querySelector(".product__bundle-box-wrapper")) {
          document.querySelector(".product__bundle-box-wrapper").innerHTML = "";
        }
      }
    }

    let form = selected.closest(".product-form");

    // If it's a bundle then populate the final selection and update the price
    if (document.querySelector("[data-bundle-container]")) {
      let formIndex = form.closest(".bundle__product-container").dataset
        .index;

      // Search the value in the master select
      let selectedVariantImage;
      let selectedVariantTitle;
      let selectedVariantPrice;
      let selectedAvailability = true;
      if (option) {
        selectedVariantImage = option.dataset.image;
        selectedVariantTitle = option.dataset.title;
        selectedVariantPrice = option.dataset.price;
        if (option.hasAttribute("disabled")) {
          selectedAvailability = false;
        }
      }

      // If the variant selected is the first
      if (
        selected.closest(".product__selectors").querySelector("#optional-id")
      ) {
        //Let's select the corresponding option of the optional product, same color and same size of the main product
        const $select = document.getElementById("optional-id");
        const $options = Array.from($select.options);
        const optionToSelect = $options.find(
          (item) => item.text === selectedVariantName
        );
        if (optionToSelect) {
          optionToSelect.selected = true;
        }
      }

      let bundleFinalCell = document.querySelector(
        '.product__bundle-variant[data-index="' + formIndex + '"]'
      );
      bundleFinalCell
        .querySelector(".product__bundle-variant-image")
        .setAttribute("src", selectedVariantImage);
      bundleFinalCell.querySelector("h3").innerHTML = selectedVariantTitle;
      bundleFinalCell.setAttribute("data-price", selectedVariantPrice);
      bundleFinalCell.setAttribute("data-availability", selectedAvailability);

      // Get all the bundles individual item prices and sum it up
      var bundleFinalPrice = 0;
      var variantUnavailable = false;
      document
        .querySelectorAll(".product__bundle-variant")
        .forEach((variant) => {
          if (variant.dataset.price) {
            bundleFinalPrice =
              parseInt(bundleFinalPrice) + parseInt(variant.dataset.price);
          }
          if (
            variant.dataset.availability &&
            variant.dataset.availability === "false"
          ) {
            variantUnavailable = true;
          }
        });

      let button = selected
        .closest(".bundle__form")
        .querySelector(".product__bundle__submit__add");
      let buttonText = button.querySelector("[data-add-to-cart-text]");
      if (variantUnavailable) {
        //disable add to cart and change the text
        button.setAttribute("disabled", "");
        buttonText.innerHTML = window.theme.strings.soldOut;
      } else {
        button.removeAttribute("disabled");
        buttonText.innerHTML = window.theme.strings.addToCart;
      }

      var optional_selected = document.querySelector(
        'input[name="optional"]:checked'
      ).value;
      if (optional_selected == 1) {
        var optional_price = document.querySelector(
          'input[name="optional-price"]'
        ).value;
        bundleFinalPrice = bundleFinalPrice + parseInt(optional_price);
      }

      //let's find ig the additional product is selected and get the price and sum it up
      bundleFinalPrice = themeVendor.themeCurrency.formatMoney(
        bundleFinalPrice,
        theme.moneyFormat
      );
      document.querySelector("[data-product-price]").innerHTML =
        bundleFinalPrice;
    }
  }

  _onOptionalBundleProductChange() {
    console.log('[ProductFormManager] _onOptionalBundleProductChange');
    const value = this.value;

    if (value == 1) {
      // We need to get the price and sum with the actual price
      const price = this.closest(".optional-product__fieldset").querySelector(
        'input[name="optional-price"]'
      ).value;

      let bundleFinalPrice = 0;
      document
        .querySelectorAll(".product__bundle-variant")
        .forEach((variant) => {
          if (variant.dataset.price) {
            bundleFinalPrice =
              parseInt(bundleFinalPrice) + parseInt(variant.dataset.price);
          }
        });

      //let's find ig the additional product is selected and get the price and sum it up
      bundleFinalPrice = bundleFinalPrice + parseInt(price);
      bundleFinalPrice = themeVendor.themeCurrency.formatMoney(
        bundleFinalPrice,
        theme.moneyFormat
      );
      document.querySelector("[data-product-price]").innerHTML =
        bundleFinalPrice;
    } else {
      // if deselect, then we need to adjust the price to only the sum of the selected variants
      var bundleFinalPrice = 0;
      document
        .querySelectorAll(".product__bundle-variant")
        .forEach((variant) => {
          if (variant.dataset.price) {
            bundleFinalPrice =
              parseInt(bundleFinalPrice) + parseInt(variant.dataset.price);
          }
        });

      bundleFinalPrice = themeVendor.themeCurrency.formatMoney(
        bundleFinalPrice,
        theme.moneyFormat
      );
      document.querySelector("[data-product-price]").innerHTML =
        bundleFinalPrice;
    }
  }

  _attachListeners() {
    console.log('[ProductFormManager] _attachListeners');
    this.container
      .querySelectorAll(
        '.product__selectors input[type="radio"]:not(.optional-product__radio)'
      )
      .forEach((radio) => {
        radio.addEventListener("change", this._onVariantSelected.bind(this));
      });

    /* Update the Shop Pay form below the price */
    this.container
      .querySelector('.product-form input[name="id"]')
      ?.addEventListener("change", (event) => {
        const value = event.currentTarget?.value;
        var shopPayFormVariantId = this.container.querySelector(
          '.shop-pay-terms input[name="id"]'
        );
        if (shopPayFormVariantId) {
          shopPayFormVariantId.value = value;
          let form = shopPayFormVariantId.closest("form");
          let e = new Event("change");
          form.dispatchEvent(e);


        }
      });


  }


}

window.sections.register("product", ProductFormManager);


// Bundle
(() => {
  const handleVariantChange = (evt) => {
    const container = evt.target.closest("div[data-bundle-container]");
    const titleIconCircle = container.querySelector(
      ".bundle__title svg circle"
    );
    const titleIconPath = container.querySelector(".bundle__title svg path");

    titleIconCircle.style.stroke = "#343230";
    titleIconPath.style.stroke = "#343230";
  };

  const toggleDrawerHeight = (evt) => {
    evt.preventDefault();

    const container = evt.target.closest("div[data-bundle-container]");
    const title = container.querySelector(".bundle__title-wrapper");
    const icon = container.querySelector(".bundle__title-drawer__icon svg");

    if (container.dataset.open === "true") {
      container.style.height = `${title.clientHeight}px`;
      container.dataset.open = "false";
    } else {
      container.style.height = `${container.dataset.height}px`;
      container.dataset.open = "true";
    }
  };

  document.querySelectorAll("div[data-bundle-container]").forEach((node) => {
    node.dataset.height = node.clientHeight;
    node.dataset.open = "true";
    node.style.height = `${node.clientHeight}px`;
  });

  document.querySelectorAll(".bundle__title-wrapper").forEach((node) => {
    node.addEventListener("click", toggleDrawerHeight);
  });

  document
    .querySelectorAll(
      "div[data-bundle-container] .swatch__button:not(.soldout)"
    )
    .forEach((node) => {
      node.addEventListener("click", handleVariantChange);
    });
})();


// Upsell
if (!customElements.get('product-upsell')) {
  class ProductUpsell extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.container = this
      if (!this.container) return;

      this.upsells = [];
      this.productForm = this.container.closest('form');
      if (!this.productForm) {
        console.warn('ProductUpsell: Could not find "form.product-form"');
        return;
      }

      this.originalProductInput = this.productForm.querySelector('input[name="id"]');
      if (!this.originalProductInput) {
        console.warn('ProductUpsell: Could not find input[name="id"] in form');
        return;
      }

      this.addRemoveProduct = this.addRemoveProduct.bind(this);
      this.showHideProduct = this.showHideProduct.bind(this);

      this.querySelectorAll('.product-upsell__check input[type="checkbox"]').forEach((item) => {
        item.addEventListener('change', this.addRemoveProduct, { passive: true });
      });

      this.productTemplate = this.closest('#template-product');
      if (this.productTemplate) {
        this.productTemplate.addEventListener('theme:variant:change', this.showHideProduct, { passive: true });
      } else {
        console.warn('ProductUpsell: Could not find ancestor "#template-product", listening on document.');
        document.addEventListener('theme:variant:change', this.showHideProduct, { passive: true });
      }
    }

    disconnectedCallback() {
      if (!this.container || !this.productForm) return;

      this.querySelectorAll('.product-upsell__check input[type="checkbox"]').forEach((item) => {
        item.removeEventListener('change', this.addRemoveProduct);
      });

      if (this.productTemplate) {
        this.productTemplate.removeEventListener('theme:variant:change', this.showHideProduct);
      } else {
        document.removeEventListener('theme:variant:change', this.showHideProduct);
      }

      if (this.upsells.length > 0) {
        this.restoreFormIds();
      }
    }

    updateOriginalProductName() {
      this.originalProductInput.name = 'id[]';
    }

    restoreFormIds() {
      this.originalProductInput.name = 'id';
      this.upsells.forEach((upsell) => {
        upsell.remove();
      });
      this.upsells = [];
    }

    addFormId(id) {
      const newInput = document.createElement('input');
      newInput.type = 'hidden';
      newInput.name = 'id[]';
      newInput.value = id;
      this.productForm.appendChild(newInput);
      this.upsells.push(newInput);
    }

    removeFormId(id) {
      const index = this.upsells.findIndex((item) => item.value === id);
      if (index > -1) {
        this.upsells[index].remove();
        this.upsells.splice(index, 1);
      }
    }

    addRemoveProduct(evt) {
      if (!this.upsells.length && evt.target.checked) {
        this.updateOriginalProductName();
      }

      if (evt.target.checked) {
        this.addFormId(evt.target.value);
      } else {
        this.removeFormId(evt.target.value);
      }

      if (!this.upsells.length) {
        this.restoreFormIds();
      }
    }

    showHideWrapper() {
      if (!this.container.querySelector('label.product-upsell:not(.hidden)')) {
        this.container.classList.add('hidden');
      } else {
        this.container.classList.remove('hidden');
      }
    }

    showHideProduct(evt) {
      console.log("🚀 ~ ProductUpsell ~ showHideProduct ~ evt:", evt)
      if (!evt.detail.variant) return;

      this.querySelectorAll(`label.product-upsell[data-variant-id="${evt.detail.variant.id}"]`).forEach((item) => {
        item.classList.remove('hidden');
      });

      this.querySelectorAll(`label.product-upsell:not([data-variant-id="${evt.detail.variant.id}"])`).forEach((item) => {
        item.classList.add('hidden');
      });

      this.showHideWrapper();
    }
  }
  customElements.define('product-upsell', ProductUpsell);
}

